import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import LinkExt from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Heading2, Undo, Redo, Image as ImageIcon, Loader2, AlignLeft, AlignCenter, AlignRight, AlignJustify } from "lucide-react";
import DOMPurify from "dompurify";
import { sanitizeRichHtml } from "@/lib/richHtml";
import { toast } from "sonner";
import Image from "@tiptap/extension-image";
import { fileUrl } from "@/lib/api";
import { uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload } from "@/lib/uploadLimits";
import { cn } from "@/lib/utils";
import { activeMentionToken, linkifyMentionsHtml, MENTION_RE_SRC } from "@/lib/mentions";
import { useMentionSearch, MentionDropdown } from "@/components/social/MentionAutocomplete";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";

// View-only ProseMirror decoration that paints a *confirmed* @handle teal while
// composing. Confirmed = handles the user actually picked from the @ dropdown
// (any profile type), tracked in `confirmedRef`. Crucially this NEVER mutates
// the document, so `editor.getHTML()` still returns the same plain `@handle `
// text and the post→render→teal pipeline is completely unchanged.
const MentionHighlight = (confirmedRef) =>
  Extension.create({
    name: "mentionHighlight",
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("mentionHighlight"),
          props: {
            decorations(state) {
              const set = confirmedRef.current;
              if (!set || !set.size) return DecorationSet.empty;
              const re = new RegExp(MENTION_RE_SRC, "g");
              const decos = [];
              state.doc.descendants((node, pos) => {
                if (!node.isText || !node.text) return;
                const text = node.text;
                re.lastIndex = 0;
                let m;
                while ((m = re.exec(text)) !== null) {
                  if (!set.has(m[2].toLowerCase())) continue;
                  const from = pos + m.index + m[1].length;
                  const to = from + 1 + m[2].length;
                  decos.push(Decoration.inline(from, to, { class: "so-mention-live" }));
                }
              });
              return DecorationSet.create(state.doc, decos);
            },
          },
        }),
      ];
    },
  });

const Btn = ({ active, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={cn(
      "inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 hover:bg-secondary",
      active && "bg-teal-50 text-primary"
    )}
  >
    {children}
  </button>
);

// Available toolbar buttons; pass a subset via the `toolbar` prop to lock the
// editor to a constrained format (e.g. ["bold","italic","underline","link"]).
const DEFAULT_TOOLBAR = ["bold", "italic", "heading", "bullet", "ordered", "link"];

// Full long-form preset — headings dropdown (P/H2/H3/H4), alignment and image
// upload. Used on profile background, org about/solving/good-fit, event &
// resource descriptions.
export const FULL_TOOLBAR = ["headings", "bold", "italic", "underline", "align", "bullet", "ordered", "link", "image"];

export function RichTextEditor({ value, onChange, placeholder = "Write something…", testId, mentions = false, emoji = false, toolbar = DEFAULT_TOOLBAR, editorClassName = "", knownHandles, onSubmit }) {
  const { query, setQuery, results, setResults } = useMentionSearch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [coords, setCoords] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const rangeRef = useRef(null);
  const openRef = useRef(false);
  const resultsRef = useRef([]);
  const activeRef = useRef(0);
  const selectRef = useRef(() => {});
  const wrapRef = useRef(null);
  const imageInputRef = useRef(null);
  // Kept in a ref so the editor's keymap always calls the LATEST submit handler without
  // the editor having to be re-created on every render.
  const submitRef = useRef(onSubmit);
  useEffect(() => { submitRef.current = onSubmit; }, [onSubmit]);
  // Set of confirmed (dropdown-selected) handles, lowercased, for the live teal
  // highlight. Seeded from knownHandles so pre-existing tags show teal in edit mode.
  const confirmedRef = useRef(null);
  if (confirmedRef.current === null) {
    confirmedRef.current = new Set((knownHandles || []).map((h) => String(h).toLowerCase()));
  }

  const open = mentions && query !== null && results.length > 0;
  useEffect(() => { openRef.current = open; resultsRef.current = results; activeRef.current = activeIndex; });

  const close = () => { setQuery(null); setResults([]); rangeRef.current = null; setCoords(null); setActiveIndex(0); };

  const detect = (ed) => {
    const { state } = ed;
    const sel = state.selection;
    if (!sel.empty) { close(); return; }
    const from = sel.from;
    const startPos = sel.$from.start();
    const textBefore = state.doc.textBetween(startPos, from, "\n", "\n");
    const tok = activeMentionToken(textBefore, textBefore.length);
    if (tok && tok.query.length >= 1) {
      rangeRef.current = { from: startPos + tok.start, to: from };
      setQuery(tok.query);
      setActiveIndex(0);
      try {
        const c = ed.view.coordsAtPos(from);
        const wrap = wrapRef.current;
        if (wrap) {
          const wr = wrap.getBoundingClientRect();
          const left = Math.min(Math.max(0, c.left - wr.left), Math.max(0, wr.width - 300));
          setCoords({ left, top: c.bottom - wr.top + 6 });
        }
      } catch (_) { /* position not measurable yet */ }
    } else {
      close();
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] }, link: false, underline: false }),
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
      // `defaultProtocol: https` matters: a member typing a bare domain ("acme.co") used
      // to be autolinked as http, and our sanitizer allows https/mailto only — so the
      // href was stripped on save, leaving a dead link (and killing its preview card).
      LinkExt.configure({ openOnClick: false, autolink: true, defaultProtocol: "https",
                          protocols: ["https", "http", "mailto"] }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ...(mentions ? [MentionHighlight(confirmedRef)] : []),
    ],
    content: value || "",
    onUpdate: ({ editor }) => { onChange(editor.getHTML()); if (mentions) detect(editor); },
    onSelectionUpdate: ({ editor }) => { if (mentions) detect(editor); },
    editorProps: {
      attributes: { class: cn("tiptap prose-so min-h-[140px] px-3 py-2.5 focus:outline-none", toolbar.includes("headings") && "prose-so-spacious", editorClassName) },
      handleKeyDown: (_view, event) => {
        // Ctrl+Enter (Cmd+Enter on a Mac) submits, and is checked BEFORE the mention
        // dropdown so the shortcut works even while a @mention list is open. Plain Enter
        // is left alone: this is a rich multi-paragraph editor, so Enter is a new line.
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)
            && submitRef.current) {
          event.preventDefault();
          submitRef.current();
          return true;
        }
        if (!openRef.current) return false;
        const n = resultsRef.current.length;
        if (!n) return false;
        if (event.key === "ArrowDown") { setActiveIndex((i) => (i + 1) % n); return true; }
        if (event.key === "ArrowUp") { setActiveIndex((i) => (i - 1 + n) % n); return true; }
        if (event.key === "Enter" || event.key === "Tab") { selectRef.current(resultsRef.current[activeRef.current]); return true; }
        if (event.key === "Escape") { close(); return true; }
        return false;
      },
    },
  });

  const select = (item) => {
    if (!editor || !item) return;
    const r = rangeRef.current;
    if (!r) return;
    confirmedRef.current.add(String(item.handle).toLowerCase());
    editor.chain().focus().insertContentAt({ from: r.from, to: r.to }, `@${item.handle} `).run();
    close();
  };
  useEffect(() => { selectRef.current = select; });

  // Seed/refresh confirmed handles when edit-mode mentions arrive (so existing
  // tags render teal). Only repaints when something new is actually added.
  useEffect(() => {
    if (!editor || !knownHandles || !knownHandles.length) return;
    let added = false;
    knownHandles.forEach((h) => {
      const k = String(h).toLowerCase();
      if (!confirmedRef.current.has(k)) { confirmedRef.current.add(k); added = true; }
    });
    if (added) editor.view.dispatch(editor.state.tr);
  }, [knownHandles, editor]);

  React.useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt("Enter URL");
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url }).run();
  };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !editor) return;
    const check = validateUpload(f, "image");
    if (!check.ok) { toast.error(check.message); return; }
    setUploadingImg(true);
    try {
      const url = await uploadFile(f, "image");
      editor.chain().focus().setImage({ src: fileUrl(url), alt: f.name }).run();
    } catch {
      toast.error("Could not upload image.");
    } finally { setUploadingImg(false); }
  };

  return (
    <div ref={wrapRef} className="relative rounded-lg border border-input bg-card" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-1.5 py-1">
        {toolbar.includes("headings") && (
          <select
            aria-label="Text style"
            className="mr-1 h-8 rounded-md border border-input bg-card px-2 text-sm text-foreground/80 hover:bg-secondary focus:outline-none"
            value={editor.isActive("heading", { level: 2 }) ? "2" : editor.isActive("heading", { level: 3 }) ? "3" : editor.isActive("heading", { level: 4 }) ? "4" : "p"}
            onChange={(e) => {
              const v = e.target.value;
              const chain = editor.chain().focus();
              if (v === "p") chain.setParagraph().run();
              else chain.setHeading({ level: Number(v) }).run();
            }}
            data-testid={testId ? `${testId}-heading-select` : "rte-heading-select"}
          >
            <option value="p">Paragraph</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
          </select>
        )}
        {toolbar.includes("bold") && <Btn label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>}
        {toolbar.includes("italic") && <Btn label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>}
        {toolbar.includes("underline") && <Btn label="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Btn>}
        {toolbar.includes("align") && (
          <>
            <Btn label="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Btn>
            <Btn label="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Btn>
            <Btn label="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Btn>
            <Btn label="Justify" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify className="h-4 w-4" /></Btn>
          </>
        )}
        {toolbar.includes("heading") && <Btn label="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>}
        {toolbar.includes("bullet") && <Btn label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>}
        {toolbar.includes("ordered") && <Btn label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>}
        {toolbar.includes("link") && <Btn label="Link" active={editor.isActive("link")} onClick={setLink}><Link2 className="h-4 w-4" /></Btn>}
        {toolbar.includes("image") && (
          <>
            <Btn label="Insert image" onClick={() => !uploadingImg && imageInputRef.current?.click()}>
              {uploadingImg ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            </Btn>
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid={testId ? `${testId}-image-input` : "rte-image-input"} />
          </>
        )}
        {emoji && <EmojiPickerButton onPick={(e) => editor.chain().focus().insertContent(e).run()} side="bottom" align="start" className="h-8 w-8 text-foreground/70 hover:bg-secondary hover:text-foreground/70" testId={testId ? `${testId}-emoji` : "rte-emoji"} />}
        <div className="ml-auto flex">
          <Btn label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo className="h-4 w-4" /></Btn>
          <Btn label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo className="h-4 w-4" /></Btn>
        </div>
      </div>
      <EditorContent editor={editor} />
      {open && (
        <MentionDropdown items={results} activeIndex={activeIndex} onSelect={select} onHover={setActiveIndex} floating coords={coords} />
      )}
    </div>
  );
}

export function RichTextView({ html, className, mentions }) {
  const navigate = useNavigate();
  if (!html || html === "<p></p>") return null;
  const hasMentions = Array.isArray(mentions) && mentions.length > 0;
  const safe = hasMentions ? linkifyMentionsHtml(html, mentions) : sanitizeRichHtml(html);
  const onClick = (e) => {
    const a = e.target.closest && e.target.closest("a[data-mention]");
    if (!a) return;
    const href = a.getAttribute("href");
    if (href && href.startsWith("/")) { e.preventDefault(); navigate(href); }
  };
  return (
    <div
      onClick={onClick}
      className={cn("prose-so text-[14px] leading-7 text-foreground", className)}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
